/**
 * Component for displaying a single question in review/results mode
 * Used in QuizResults and AssessmentResults
 */

'use client';

import React, { useMemo } from 'react';
import { Question } from '../types';
import { QuizItem } from '@/store/assessment-store';
import { AssessmentQuestion } from '../AssessmentQuestion';
import { getQuestionType } from '../utils/questionUtils';
import { optionMatchesCorrect } from '../utils/correctnessUtils';

interface QuestionReviewProps {
  question: Question;
  questionType: string | null;
  questionIndex: number;
  totalQuestions: number;
  quiz: QuizItem[];
  correctAnswers: string | string[] | boolean | null;
  quizData?: any;
  result?: {
    results?: Array<{
      questionUid: string;
      result: boolean;
      selectedOptions: string | string[] | boolean;
      correctAnswer?: string | string[] | boolean;
    }>;
  };
  showCorrectness?: boolean; // Only show correctness for quiz, not assessment
}

export function QuestionReview({
  question,
  questionType,
  questionIndex,
  totalQuestions,
  quiz,
  correctAnswers,
  quizData,
  result,
  showCorrectness = true, // Default to true for quiz results
}: QuestionReviewProps) {
  // Dummy handlers for review mode (not used but required by AssessmentQuestion)
  const dummySetQuizData = () => {};
  const dummyLearningPathwayUID = 'results-review';

  // Correctness checker: for each option return true (correct) or false (wrong) so we can highlight all options with border + Correct/Incorrect label
  // When correctAnswers is shown below ("The correct answer was: X"), use it as primary source so borders match the displayed text
  const correctnessChecker = useMemo(() => {
    const questionUid = question.metadata_uid;

    // Get options for index-based correctAnswer (API may return 0, 1, 2, ...)
    const allOptions =
      question.single_choice_question?.answers ??
      question.multiple_choice_question?.answers ??
      (question.true_or_false_question ? [true, false] : []);

    // Helper: build checker from correct value
    const buildChecker = (correct: string | string[] | boolean | number) => ({
      check: (option: string | boolean): boolean =>
        optionMatchesCorrect(option, correct, allOptions),
    });

    // 1. PRIMARY: Use correctAnswers when available (same value shown in "The correct answer was:" below)
    // This guarantees option borders match the displayed correct answer text
    if (correctAnswers != null) {
      let correct: string | string[] | boolean;
      if (correctAnswers === 'true') correct = true;
      else if (correctAnswers === 'false') correct = false;
      else if (typeof correctAnswers === 'string' && correctAnswers.includes(', '))
        correct = correctAnswers.split(', ').map((s) => s.trim());
      else correct = typeof correctAnswers === 'string' ? correctAnswers.trim() : correctAnswers;

      return buildChecker(correct);
    }

    // 2. Use API results when available (no fallback to CMS parsing)
    const apiResults = result?.results;
    if (apiResults?.length) {
      const apiResult = apiResults.find(
        (r: { questionUid?: string; question_uid?: string }) =>
          (r.questionUid ?? r.question_uid) === questionUid
      );
      const correct =
        apiResult?.correctAnswer ??
        (apiResult as { correct_answer?: string | string[] | boolean | number })?.correct_answer;
      if (apiResult && correct !== undefined && correct !== null) {
        return buildChecker(correct);
      }
    }

    return undefined;
  }, [questionIndex, question, result?.results, correctAnswers]);

  if (!questionType) {
    return null;
  }

  return (
    <AssessmentQuestion
      question={question}
      questionType={questionType}
      questionIndex={questionIndex}
      totalQuestions={totalQuestions}
      quiz={quiz}
      learningPathwayUID={dummyLearningPathwayUID}
      setQuizData={dummySetQuizData}
      onNext={() => {}}
      onPrevious={() => {}}
      isQuestionCardOpen={true}
      isQuiz={true}
      correctAnswers={correctAnswers}
      quizData={quizData}
      hideNavigation={true}
      showCorrectness={showCorrectness}
      correctnessChecker={showCorrectness && correctnessChecker ? correctnessChecker : undefined}
    />
  );
}
